import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ImportReport } from 'src/app/models/gum-models/import-report.model';
import { ListOptions } from 'src/app/models/gum-models/list-options';
import { Organization } from 'src/app/models/organization.model';
import { ServiceBase } from './service-base';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService extends ServiceBase {
  private importFinished = new Subject<ImportReport>();

  private currentOrganizationSubject: BehaviorSubject<Organization | null>;

  constructor(private http: HttpClient) {
    super();
    const currentOrgJson = sessionStorage.getItem('currentOrganization');
    const currentOrg = currentOrgJson
      ? (JSON.parse(currentOrgJson) as Organization)
      : null;
    this.currentOrganizationSubject = new BehaviorSubject<Organization | null>(
      currentOrg
    );
  }

  public get onImportFinished$() {
    return this.importFinished.asObservable();
  }

  public get currentOrganizationChange$() {
    return this.currentOrganizationSubject.asObservable();
  }

  getOrganisation(
    organizationId: string,
    globalNotification = true,
    executeOption = false
  ): Observable<Organization> {
    const orgId =
      executeOption && this.currentOrganizationSubject.value?.id
        ? this.currentOrganizationSubject.value.id
        : '';
    const httpOptions = {
      headers: new HttpHeaders()
        .set('global-notification', String(globalNotification))
        .set('X-OrganizationId', orgId),
    };

    return this.http.get<Organization>(
      `${this.apiUrl}/api/gum/organizations/${organizationId}`,
      httpOptions
    );
  }

  getOrganizationWithDisplayParentOrganization(
    organizationId: string
  ): Observable<Organization> {
    return this.getOrganisation(organizationId).pipe(
      switchMap((organization: Organization) => {
        const parentId = organization.organizationId;

        if (!parentId) {
          console.warn('[WARN] Aucune organizationId définie.');
          organization.displayParentOrganization = '';
          return of(organization);
        }

        return this.getOrganisation(parentId, false).pipe(
          map((parentOrg) => {
            organization.displayParentOrganization = parentOrg.display;
            return organization;
          }),
          catchError((err) => {
            console.error('[ERREUR] Impossible de récupérer la parentOrg', {
              parentId,
              err,
            });
            organization.displayParentOrganization = `[${parentId}]`;
            return of(organization);
          })
        );
      })
    );
  }

  getOrganisations(
    executeOption: boolean = true,
    filter: string | null = null,
    listOptions?: ListOptions
  ): Observable<Organization[]> {
    const orgId =
      executeOption && this.currentOrganizationSubject.value?.id
        ? this.currentOrganizationSubject.value.id
        : '';
    let headers = new HttpHeaders().set('X-OrganizationId', orgId);

    if (listOptions) {
      headers = listOptions.addListOptions(headers);
    }

    const urlString = filter
      ? `${this.apiUrl}/api/gum/organizations?filter=${encodeURIComponent(
          filter
        )}`
      : `${this.apiUrl}/api/gum/organizations`;

    return this.http.get<Organization[]>(urlString, { headers }).pipe(
      map((orgs) => {
        orgs.forEach((org) => {
          const parentOrg = orgs.find((x) => x.id === org.organizationId);
          org.displayParentOrganization = parentOrg ? parentOrg.display : '';
        });
        return orgs;
      })
    );
  }

  getCurrentOrganization(): Organization | null {
    return this.currentOrganizationSubject.value;
  }

  setCurrentOrganization(organization: Organization): void {
    console.log('[OrganizationsService] SET ORG', organization);

    sessionStorage.setItem('currentOrganization', JSON.stringify(organization));
    this.currentOrganizationSubject.next(organization);
  }

  removeCurrentOrganization(): void {
    sessionStorage.removeItem('currentOrganization');
    this.currentOrganizationSubject.next(null);
  }

  private searchChildren(
    source: Organization[],
    result: Organization[],
    root: Organization
  ): void {
    result.push(root);
    source.splice(
      source.findIndex((o) => o.display === root.display),
      1
    );

    source
      .filter((o) => o.displayParentOrganization === root.display)
      .forEach((child) => this.searchChildren(source, result, child));
  }

  /**
   * Active ou suspend une organisation (partenaire).
   * @param organizationId ID de l'organisation à modifier
   * @param enabled true pour activer, false pour suspendre
   */
  setOrganizationEnabled(
    organizationId: string,
    enabled: boolean
  ): Observable<Organization> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.patch<Organization>(
      `${this.apiUrl}/api/gum/organizations/${organizationId}/enabled`,
      enabled,
      { headers }
    );
  }
}
